import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useNotification, useUserData } from '../../../../hooks'
import { preventDefault } from '../../../../utils/events'
import { isStaffUser } from '../../../../utils/user'
import { useFileDropzone, useFileUpload } from '../../../File'
import {
  DOCUMENT_ALL_PERMISSION_GROUPS_ALLOW,
  DROP_TARGET_DATA_KEY,
} from '../../Document.constants'
import { DocumentEditAccessActionModal } from '../../DocumentEditAccessActionModal'

export function useDocumentsUploadFile({
  outboundUuid,
  parent,
  permissionGroups,
  renderUploadDestinationModal,
  skipAccessModal,
  onAfterUpload,
  onCloseModal,
  onReload,
  onSetModal,
}) {
  const [files, setFiles] = useState(EMPTY_LIST)
  const [hoveredId, setHoveredId] = useState(null)

  const { t } = useTranslation()

  const {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const user = useUserData()

  const {
    filesUploading,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  const handleCloseModal = useCallback(() => {
    setHoveredId(null)

    onCloseModal()
  }, [onCloseModal])

  const handleFinishUploadFiles = useCallback(
    (acceptedFiles, errorFiles) => {
      handleUploadFile(acceptedFiles, errorFiles)

      setHoveredId(null)

      if (acceptedFiles.length > 0) {
        handleAddSuccessNotification({
          title: t('Notification.FileSuccessUploadKey', {
            count: acceptedFiles.length,
            totalCount: acceptedFiles.length + errorFiles.length,
          }),
        })
      }

      if (errorFiles.length > 0) {
        handleAddErrorNotification({
          title: t('Notification.FileErrorUploadKey', {
            count: errorFiles.length,
            totalCount: acceptedFiles.length + errorFiles.length,
          }),
        })
      }

      onAfterUpload?.(acceptedFiles)

      onReload()
    },
    [handleUploadFile, onAfterUpload, onReload]
  )

  const handleSubmitAccess = useCallback(
    ({ errors, filesList, values, onUpload }) => {
      onCloseModal()

      if (!hoveredId) {
        handleStartUploading(filesList)
      }

      handleAddInfoNotification('Notification.FileStartLoadingInfoKey')

      onUpload({
        errors,
        files: filesList,
        params: {
          parent: hoveredId || parent,
          set_permission_groups: values.set_permission_groups,
        },
      })
    },
    [handleStartUploading, hoveredId, parent]
  )

  const handleOpenAccessModal = useCallback(
    uploadProps => {
      onSetModal(
        <DocumentEditAccessActionModal
          checkNoChanges={false}
          initialValues={DOCUMENT_ALL_PERMISSION_GROUPS_ALLOW}
          onClose={handleCloseModal}
          onSubmit={values => handleSubmitAccess({ values, ...uploadProps })}
        />
      )
    },
    [handleSubmitAccess]
  )

  const handleFilesSelected = useCallback(
    ({ errors, filesList, onUpload }) => {
      if (filesList.length === 0) {
        return
      }

      if (isStaffUser(user) && !skipAccessModal) {
        const uploadProps = { errors, filesList, onUpload }

        function handleConfirm() {
          handleOpenAccessModal(uploadProps)
        }

        // `renderUploadDestinationModal` may return `null` when the upload
        // destination is already known (e.g. user picked it from a dropdown
        // button), in which case the modal must be skipped.
        const destinationModal = renderUploadDestinationModal?.({
          onClose: handleCloseModal,
          onConfirm: handleConfirm,
        })

        if (destinationModal) {
          onSetModal(destinationModal)
        } else {
          handleConfirm()
        }
      } else {
        if (!hoveredId) {
          handleStartUploading(filesList)
        }

        handleAddInfoNotification('Notification.FileStartLoadingInfoKey')

        onUpload({
          errors,
          files: filesList,
        })
      }
    },
    [
      handleCloseModal,
      handleOpenAccessModal,
      hoveredId,
      onSetModal,
      parent,
      renderUploadDestinationModal,
      skipAccessModal,
      user,
    ]
  )

  const handleStartCheckFiles = useCallback(() => {
    onReload()
  }, [onReload])

  const {
    getInputProps,
    getRootProps: getRootPropsBase,
    open,
  } = useFileDropzone({
    outboundUuid,
    uploadParams: {
      ...(permissionGroups
        ? { set_permission_groups: permissionGroups }
        : null),
      parent: hoveredId || parent,
    },
    onCloseModal: handleCloseModal,
    onFinishUpload: handleFinishUploadFiles,
    onPreload: handleFilesSelected,
    onSetModal: onSetModal,
    onStartCheckFiles: handleStartCheckFiles,
    onUploadErrors: handleUploadErrors,
  })

  const handleClickUploadFile = useCallback(() => {
    open()
  }, [open])

  const getRootDnDProps = useCallback(
    (userProps = EMPTY_OBJECT) => {
      const baseProps = getRootPropsBase(userProps)

      function onDragOver(e) {
        preventDefault(e)

        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'copy'
        }

        const el = e?.target?.closest(`[${DROP_TARGET_DATA_KEY}]`)

        setHoveredId(el?.dataset?.dropTarget ?? null)
        userProps.onDragOver?.(e)
      }

      function onDragLeave(e) {
        setHoveredId(null)

        if (userProps.onDragLeave) {
          userProps.onDragLeave(e)
        }
      }

      return {
        ...baseProps,
        onDragLeave,
        onDragOver,
      }
    },
    [getRootPropsBase]
  )

  return {
    filesUploading,
    getRootDnDProps,
    getUploadFileInputProps: getInputProps,
    handleClickUploadFile,
    hoveredId,
  }
}
